// Hooks' picker (live's `hooks-popover` over the shared `catalog-popover`, spec G14-templates):
// the card's hooks as cards (each poster, its clip on hover, its name, live's scene hint), the
// picks staged in a "Selected" panel with The Absurd Prop's object, and Apply.
import { Button, CloseButton, Image, Loader, Popover, Text, TextInput, Tooltip, UnstyledButton } from "@mantine/core";
import { Check, Clapperboard } from "lucide-react";
import { useState } from "react";

import type { TemplateOptionView } from "../../../api/generated/model";
import { HoverVideo } from "../../../ui/HoverVideo";
import classes from "./forms.module.css";
import { hookIdOf, inOrder, PROP_HOOK, type HookId } from "./hooks";

/** Live's words (`constants.ts`). */
const HOOKS_TIP =
  "Choose the opening-shot concept for your ad from 9 hook styles (e.g. The Impact, The Faceplant " +
  "Recovery, The Absurd Prop).";
const OBJECT_LABEL = "What should the actor hold?";
/** Live's `MAX_HELD_OBJECT_LENGTH`. */
const MAX_OBJECT = 40;

export interface HooksPickerProps {
  picked: readonly HookId[];
  heldObject: string;
  /** The card's hooks, in live's order: names, hints, posters and clips. */
  options: readonly TemplateOptionView[];
  disabled: boolean;
  onApply: (picked: HookId[], heldObject: string) => void;
}

/**
 * Live's picker: the trigger reads "Hooks", or "Hooks (N)" once picked; the cards toggle; the
 * picks wait in "Selected", in live's order, until Apply, which closes the picker. Closed
 * without Apply, nothing changes.
 */
export function HooksPicker(props: HooksPickerProps) {
  const { picked, heldObject, options, disabled, onApply } = props;
  const [opened, setOpened] = useState(false);
  const [staged, setStaged] = useState<HookId[]>([]);
  const [object, setObject] = useState("");
  const open = () => {
    setStaged([...picked]);
    setObject(heldObject);
    setOpened(true);
  };
  const toggle = (id: HookId) =>
    setStaged((current) => (current.includes(id) ? current.filter((each) => each !== id) : inOrder([...current, id])));
  const hooks = options.flatMap((option) => {
    const id = hookIdOf(option.id);
    return id === null ? [] : [{ id, option }];
  });
  const label = picked.length === 0 ? "Hooks" : `Hooks (${picked.length})`;
  return (
    <Popover opened={opened} onChange={setOpened} position="top-start" width={792} shadow="md" trapFocus>
      <Popover.Target>
        <Tooltip label={HOOKS_TIP} multiline maw={280} withArrow disabled={opened}>
          <UnstyledButton
            className={`${classes.option} ${classes.hookTrigger}`}
            aria-label={label}
            aria-expanded={opened}
            disabled={disabled}
            onClick={() => (opened ? setOpened(false) : open())}
          >
            <Clapperboard size={20} strokeWidth={1.5} aria-hidden className={classes.hookTriggerIcon} />
            <span className={classes.hookTriggerLabel}>{label}</span>
          </UnstyledButton>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown aria-label="Hook template" className={classes.hookPicker}>
        <div className={classes.hookCatalog}>
          <Text className={classes.hookTitle}>Hook template</Text>
          {hooks.length === 0 ? (
            <Loader size="sm" aria-label="Loading the hooks" />
          ) : (
            <div className={classes.hookGrid}>
              {hooks.map(({ id, option }) => (
                <HookTile key={id} option={option} chosen={staged.includes(id)} onToggle={() => toggle(id)} />
              ))}
            </div>
          )}
        </div>
        <div className={classes.hookSelected}>
          <Text className={classes.hookTitle}>Selected</Text>
          <ul className={classes.hookPicks} aria-label="Selected">
            {hooks
              .filter(({ id }) => staged.includes(id))
              .map(({ id, option }) => (
                <li key={id} className={classes.hookPick}>
                  <span className={classes.hookPickRow}>
                    {option.poster_url === null ? null : (
                      <Image src={option.poster_url} alt="" className={classes.hookPickPoster} />
                    )}
                    <Text className={classes.hookPickName}>{option.label}</Text>
                    <CloseButton
                      size="sm"
                      aria-label={`Remove ${option.label}`}
                      onClick={() => setStaged((current) => current.filter((each) => each !== id))}
                    />
                  </span>
                  {id === PROP_HOOK ? (
                    <TextInput
                      size="xs"
                      label={OBJECT_LABEL}
                      aria-label={OBJECT_LABEL}
                      placeholder="Banana"
                      maxLength={MAX_OBJECT}
                      value={object}
                      onChange={(event) => setObject(event.currentTarget.value)}
                    />
                  ) : null}
                </li>
              ))}
          </ul>
          <Button
            fullWidth
            onClick={() => {
              onApply(inOrder(staged), object);
              setOpened(false);
            }}
          >
            Apply
          </Button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}

/** A hook's card (live's `CatalogCard`): its poster under its clip, which plays silently while
 * hovered (`HoverVideo`), its name, live's ring. */
function HookTile(props: { option: TemplateOptionView; chosen: boolean; onToggle: () => void }) {
  const { option, chosen, onToggle } = props;
  const [hovered, setHovered] = useState(false);
  const card = (
    <UnstyledButton
      className={classes.hookCard}
      aria-label={option.label}
      aria-pressed={chosen}
      onClick={onToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className={classes.hookMedia}>
        <HoverVideo src={option.clip_url} poster={option.poster_url} hovering={hovered} />
        {chosen ? (
          <span className={classes.hookBadge} aria-hidden>
            <Check size={16} strokeWidth={2} />
          </span>
        ) : null}
      </span>
      <Text className={classes.hookName}>{option.label}</Text>
    </UnstyledButton>
  );
  return option.hint === null ? (
    card
  ) : (
    <Tooltip label={option.hint} position="bottom" maw={220} multiline openDelay={500} withArrow>
      {card}
    </Tooltip>
  );
}
